import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe2, Zap, ChevronDown, Copy, Webhook } from "lucide-react";
import { toast } from "sonner";

export default function SenderDomainCard() {
  const [senderEmail, setSenderEmail] = useState("");
  const [senderName, setSenderName] = useState("Newport Stays");
  const [apiKey, setApiKey] = useState("");
  const [keyMasked, setKeyMasked] = useState({ set: false, last4: "" });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/email");
      setSenderEmail(data.sender_email || "");
      setSenderName(data.sender_name || "Newport Stays");
      setKeyMasked({
        set: data.resend_api_key_set,
        last4: data.resend_api_key_last4,
      });
      setWebhookUrl(data.webhook_url || "");
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { sender_email: senderEmail, sender_name: senderName };
      if (apiKey.trim()) body.resend_api_key = apiKey.trim();
      await api.put("/settings/email", body);
      toast.success("Sender configuration saved");
      setApiKey("");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (
      !window.confirm(
        "Remove your Resend key and revert to the shared Newport Stays sender?",
      )
    )
      return;
    setBusy(true);
    try {
      await api.put("/settings/email", { resend_api_key: "" });
      toast.success("Custom sender disconnected");
      setKeyMasked({ set: false, last4: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail || !senderEmail) {
      toast.error("Set a sender email first, then test");
      return;
    }
    setTesting(true);
    try {
      await api.post("/campaigns/test", {
        subject: "Sender verification test",
        html_content: `<p>This is a test email from Newport Stays CRM using your verified sender <strong>${senderEmail}</strong>.</p>`,
        test_email: testEmail,
      });
      toast.success(`Test sent from ${senderEmail} to ${testEmail}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setTesting(false);
    }
  };

  const copy = (val, label) => {
    navigator.clipboard.writeText(val);
    toast.success(`${label} copied`);
  };

  return (
    <form onSubmit={save} className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <Globe2 className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Deliverability</div>
          <div className="font-serif text-2xl">Verified sender domain</div>
        </div>
        {keyMasked.set && (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium bg-teal-50 text-teal-800 border border-teal-100 rounded-full px-2.5 py-1">
            <Zap className="w-3 h-3" /> Live · ends {keyMasked.last4}
          </span>
        )}
      </div>

      <div className="ns-hairline my-6" />

      <div className="grid gap-4 max-w-xl">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="ns-label">Sender name</Label>
            <Input
              className="mt-2 h-11 bg-white"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="Newport Stays"
            />
          </div>
          <div>
            <Label className="ns-label">Sender email</Label>
            <Input
              type="email"
              className="mt-2 h-11 bg-white"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              placeholder="hello@newportstays.co.uk"
            />
          </div>
        </div>
        <div>
          <Label className="ns-label">Resend API key</Label>
          <Input
            type="password"
            className="mt-2 h-11 bg-white font-mono"
            placeholder={
              keyMasked.set
                ? `••••••••••••${keyMasked.last4} (paste a new key to replace)`
                : "re_XXXXXXXXXXXX"
            }
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <div className="text-[11px] text-slate-400 mt-1.5">
            The key is stored server-side and never shown back to the client.
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <Button
            type="submit"
            disabled={busy}
            className="rounded-full ns-btn-primary h-11 px-6"
          >
            {busy ? "Saving…" : "Save sender"}
          </Button>
          {keyMasked.set && (
            <Button
              type="button"
              variant="ghost"
              onClick={disconnect}
              className="rounded-full h-11 px-4 text-slate-500 hover:text-red-600"
            >
              Disconnect
            </Button>
          )}
        </div>

        {webhookUrl && (
          <div className="mt-4 bg-stone-50 border border-stone-100 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Webhook className="w-4 h-4 text-teal-700" />
              <div className="ns-label">Bounce &amp; unsubscribe webhook</div>
            </div>
            <p className="text-xs text-slate-500 mb-2 leading-relaxed">
              In Resend → <em>Webhooks</em>, paste this URL and enable events{" "}
              <code>email.bounced</code>, <code>email.complained</code> and{" "}
              <code>email.failed</code>. Any recipient in one of those events is
              suppressed from future mail-shots automatically. Unsubscribe
              footers with a signed one-click link are appended to every email
              already.
            </p>
            <div className="flex gap-2 items-center">
              <Input
                readOnly
                value={webhookUrl}
                className="h-10 font-mono text-xs bg-white"
                onFocus={(e) => e.target.select()}
              />
              <Button
                type="button"
                variant="outline"
                className="rounded-full h-10 px-4"
                onClick={() => copy(webhookUrl, "Webhook URL")}
              >
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        {keyMasked.set && (
          <div className="mt-2 bg-stone-50 border border-stone-100 rounded-xl p-4 flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <Label className="ns-label">Test the verified sender</Label>
              <Input
                type="email"
                className="mt-2 h-11 bg-white"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={sendTest}
              disabled={testing || !testEmail}
              className="rounded-full h-11 px-5"
            >
              {testing ? "Sending…" : "Send test"}
            </Button>
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="text-xs text-teal-700 hover:underline mt-2 self-start flex items-center gap-1"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${showHelp ? "rotate-180" : ""}`}
          />
          How to verify newportstays.co.uk with Resend
        </button>

        {showHelp && (
          <div className="bg-teal-50/40 border border-teal-100 rounded-xl p-5 text-sm text-slate-700 leading-relaxed">
            <ol className="list-decimal ml-5 space-y-2">
              <li>
                Create a free account at{" "}
                <a
                  className="text-teal-700 hover:underline"
                  href="https://resend.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  resend.com
                </a>{" "}
                and add <strong>newportstays.co.uk</strong> as a domain.
              </li>
              <li>
                Resend will list three DNS records to add — an SPF entry, two
                DKIM entries and an optional DMARC entry. Paste them into your
                domain registrar (GoDaddy, 123-Reg, Namecheap, etc.).
              </li>
              <li>
                Wait 5–10 minutes and click <em>Verify</em> in Resend until all
                rows show a green tick.
              </li>
              <li>
                In Resend → <em>API Keys</em>, create a key with the &quot;Send
                emails&quot; scope and copy it (starts with <code>re_</code>).
              </li>
              <li>
                Paste the key above, set your Sender email and Save. Then copy
                the webhook URL above into Resend&apos;s Webhooks screen to
                capture bounces and unsubscribes.
              </li>
            </ol>
          </div>
        )}
      </div>
    </form>
  );
}
